import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field, Select } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import {
  LOGIST_DICTIONARIES,
  LOGIST_OWNERSHIP_TYPES,
  type LogistDictName,
  type LogistDriver,
  type LogistRollingStock,
} from '@entities/logistics';
import {
  useListLogistDictQuery,
  useCreateLogistDictMutation,
  useUpdateLogistDictMutation,
  useActivateLogistDictMutation,
  useDeactivateLogistDictMutation,
} from '@shared/api';

/**
 * ОСНОВА.Логист — справочники (ТЗ AI-LOGIST-2026 §8.2): водители, ТС, прицепы.
 * Веб-CRUD master-data. Деактивация вместо удаления (на записи ссылаются рейсы).
 */

// ---- Описание полей формы по справочнику (декларативно) --------------------

type FieldType = 'text' | 'number' | 'date' | 'checkbox' | 'select';
interface FormField {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  /** Варианты для `select`: код уходит на сервер, подпись видит человек. */
  options?: ReadonlyArray<{ code: string; label: string }>;
  /** Пояснение под полем — для реквизитов, которые задаёт формат ЭТрН. */
  hint?: string;
}

const DRIVER_FIELDS: FormField[] = [
  { key: 'full_name', label: 'ФИО', type: 'text', required: true },
  { key: 'license_series', label: 'ВУ: серия', type: 'text' },
  { key: 'license_number', label: 'ВУ: номер', type: 'text' },
  { key: 'license_valid_until', label: 'ВУ действительно до', type: 'date' },
  {
    key: 'license_issued_at',
    label: 'ВУ выдано',
    type: 'date',
    hint: 'Обязательно для накладной, если не указан ИНН водителя',
  },
  { key: 'inn', label: 'ИНН водителя', type: 'text' },
  { key: 'phone', label: 'Телефон', type: 'text' },
  { key: 'carrier_name', label: 'Перевозчик (организация)', type: 'text' },
  { key: 'operator_driver_id', label: 'ID у оператора ИС ЭПД', type: 'text' },
  { key: 'operator_registered', label: 'Зарегистрирован у оператора', type: 'checkbox' },
  { key: 'has_esignature', label: 'Есть ПЭП/УНЭП', type: 'checkbox' },
  { key: 'notes', label: 'Примечание', type: 'text' },
];

const STOCK_FIELDS: FormField[] = [
  { key: 'plate_number', label: 'Госномер', type: 'text', required: true },
  { key: 'brand', label: 'Марка', type: 'text' },
  { key: 'sts_number', label: 'СТС', type: 'text' },
  { key: 'max_permitted_mass_kg', label: 'Разрешённая макс. масса, кг', type: 'number' },
  { key: 'unladen_mass_kg', label: 'Масса без нагрузки, кг', type: 'number' },
  { key: 'load_capacity_kg', label: 'Грузоподъёмность, кг', type: 'number' },
  { key: 'owner', label: 'Собственник / арендодатель', type: 'text' },
  { key: 'is_leased', label: 'Арендованный', type: 'checkbox' },
  {
    key: 'ownership_type',
    label: 'Тип владения',
    type: 'select',
    options: LOGIST_OWNERSHIP_TYPES,
    hint: 'Уходит в накладную. Не выбран — берётся из отметки об аренде',
  },
  { key: 'vehicle_kind', label: 'Тип техники', type: 'text', placeholder: 'Самосвал' },
  { key: 'body_volume_m3', label: 'Вместимость кузова, м³', type: 'number' },
  { key: 'notes', label: 'Примечание', type: 'text' },
];

function fieldsFor(dict: LogistDictName): FormField[] {
  return dict === 'drivers' ? DRIVER_FIELDS : STOCK_FIELDS;
}

// ---- Стили -----------------------------------------------------------------

/** Пояснение под полем: почему реквизит нужен и что будет, если он пуст. */
const Hint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 22px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  max-width: 820px;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.secondary)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const FormCard = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 720px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

// ---- Хелперы ---------------------------------------------------------------

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

/** Человекочитаемый ключ строки для таблицы. */
function rowKey(dict: LogistDictName, it: LogistDriver | LogistRollingStock): string {
  if (dict === 'drivers') {
    const d = it as LogistDriver;
    return d.full_name;
  }
  const s = it as LogistRollingStock;
  return s.plate_number;
}

/** Вторая колонка таблицы — краткая сводка по записи. */
function rowSummary(dict: LogistDictName, it: LogistDriver | LogistRollingStock): string {
  if (dict === 'drivers') {
    const d = it as LogistDriver;
    const reg = d.operator_registered ? 'у оператора: да' : 'у оператора: нет';
    const esig = d.has_esignature ? 'ЭП: есть' : 'ЭП: нет';
    return [d.phone, d.carrier_name, reg, esig].filter(Boolean).join(' · ');
  }
  const s = it as LogistRollingStock;
  const mass = s.max_permitted_mass_kg != null ? `РММ ${s.max_permitted_mass_kg} кг` : null;
  return [s.brand, mass, s.owner, s.is_leased ? 'аренда' : null].filter(Boolean).join(' · ');
}

export function LogistDictionaries() {
  const { can_read, can_write } = useAgentPermission('logist');
  const [dict, setDict] = useState<LogistDictName>('drivers');
  const [values, setValues] = useState<Record<string, string>>({});
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [editId, setEditId] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useListLogistDictQuery({ dict, active: false });
  const [createDict, { isLoading: creating }] = useCreateLogistDictMutation();
  const [updateDict, { isLoading: updating }] = useUpdateLogistDictMutation();
  const [activate] = useActivateLogistDictMutation();
  const [deactivate] = useDeactivateLogistDictMutation();

  const items = useMemo(
    () => (data?.items ?? []) as (LogistDriver | LogistRollingStock)[],
    [data],
  );
  const fields = fieldsFor(dict);

  function switchDict(next: LogistDictName) {
    setDict(next);
    reset();
  }

  function reset() {
    setValues({});
    setFlags({});
    setEditId(null);
    setOk(null);
    setErr(null);
  }

  function startEdit(it: LogistDriver | LogistRollingStock) {
    const v: Record<string, string> = {};
    const f: Record<string, boolean> = {};
    for (const fld of fields) {
      const raw = Reflect.get(it, fld.key);
      if (fld.type === 'checkbox') {
        f[fld.key] = Boolean(raw);
      } else if (raw != null) {
        v[fld.key] = String(raw);
      }
    }
    setValues(v);
    setFlags(f);
    setEditId(it.id);
    setOk(null);
    setErr(null);
  }

  function buildBody(): Record<string, unknown> | null {
    const body: Record<string, unknown> = {};
    for (const fld of fields) {
      if (fld.type === 'checkbox') {
        body[fld.key] = Boolean(flags[fld.key]);
        continue;
      }
      const raw = (values[fld.key] ?? '').trim();
      if (fld.required && !raw) {
        setErr(`Поле «${fld.label}» обязательно.`);
        return null;
      }
      if (!raw) continue;
      if (fld.type === 'number') {
        const n = Number(raw);
        if (Number.isNaN(n)) {
          setErr(`Поле «${fld.label}» — число.`);
          return null;
        }
        body[fld.key] = n;
      } else {
        body[fld.key] = raw;
      }
    }
    return body;
  }

  async function submit() {
    setErr(null);
    setOk(null);
    const body = buildBody();
    if (!body) return;
    try {
      if (editId) {
        await updateDict({ dict, id: editId, body }).unwrap();
        setOk('Запись обновлена.');
      } else {
        await createDict({ dict, body }).unwrap();
        setOk('Запись создана.');
      }
      reset();
    } catch (e) {
      setErr(`Не удалось сохранить: ${apiErr(e)}`);
    }
  }

  if (!can_read) {
    return (
      <Wrap>
        <div style={{ opacity: 0.7, textAlign: 'center', marginTop: 80 }}>
          Нет доступа к разделу «ОСНОВА.Логист».
        </div>
      </Wrap>
    );
  }

  const meta = LOGIST_DICTIONARIES[dict];

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft size={16} />
        <span>ОСНОВА.Логист</span>
      </Back>
      <Title>Справочники</Title>
      <Subtitle>{meta.subtitle}</Subtitle>

      <Tabs>
        {(Object.keys(LOGIST_DICTIONARIES) as LogistDictName[]).map((d) => (
          <Tab key={d} $active={d === dict} onClick={() => switchDict(d)}>
            {LOGIST_DICTIONARIES[d].title}
          </Tab>
        ))}
      </Tabs>

      {isLoading ? (
        <div>Загрузка…</div>
      ) : items.length === 0 ? (
        <div style={{ opacity: 0.7 }}>Справочник пуст. Добавьте первую запись ниже.</div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{dict === 'drivers' ? 'ФИО' : 'Госномер'}</Th>
              <Th>Сводка</Th>
              <Th>Активна</Th>
              {can_write && <Th>Действия</Th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <Td>{rowKey(dict, it)}</Td>
                <Td>{rowSummary(dict, it)}</Td>
                <Td>{it.is_active ? 'да' : 'нет'}</Td>
                {can_write && (
                  <Td>
                    <Row>
                      <SmallBtn onClick={() => startEdit(it)}>Изменить</SmallBtn>
                      {it.is_active ? (
                        <SmallBtn onClick={() => deactivate({ dict, id: it.id })}>
                          Деактивировать
                        </SmallBtn>
                      ) : (
                        <SmallBtn onClick={() => activate({ dict, id: it.id })}>
                          Активировать
                        </SmallBtn>
                      )}
                    </Row>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {can_write && (
        <FormCard>
          <Title style={{ fontSize: 16 }}>
            {editId ? 'Редактирование записи' : 'Новая запись'}
          </Title>
          <FormGrid>
            {fields.map((fld) =>
              fld.type === 'checkbox' ? (
                <CheckRow key={fld.key}>
                  <input
                    type="checkbox"
                    checked={Boolean(flags[fld.key])}
                    onChange={(e) => setFlags((s) => ({ ...s, [fld.key]: e.target.checked }))}
                  />
                  {fld.label}
                </CheckRow>
              ) : (
                <Field key={fld.key}>
                  <Label>
                    {fld.label}
                    {fld.required ? ' *' : ''}
                  </Label>
                  {fld.type === 'select' ? (
                    <Select
                      value={values[fld.key] ?? ''}
                      onChange={(e) => setValues((s) => ({ ...s, [fld.key]: e.target.value }))}
                    >
                      <option value="">— не выбрано —</option>
                      {(fld.options ?? []).map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={
                        fld.type === 'number' ? 'number' : fld.type === 'date' ? 'date' : 'text'
                      }
                      placeholder={fld.placeholder}
                      value={values[fld.key] ?? ''}
                      onChange={(e) => setValues((s) => ({ ...s, [fld.key]: e.target.value }))}
                    />
                  )}
                  {fld.hint && <Hint>{fld.hint}</Hint>}
                </Field>
              ),
            )}
          </FormGrid>
          <Row>
            <Button onClick={submit} disabled={creating || updating}>
              {editId ? 'Сохранить' : 'Добавить'}
            </Button>
            {editId && (
              <Button $variant="secondary" onClick={reset}>
                Отмена
              </Button>
            )}
          </Row>
          {ok && <Msg>{ok}</Msg>}
          {err && <Msg $error>{err}</Msg>}
        </FormCard>
      )}
    </Wrap>
  );
}
